import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import CreateOrganizationModal from 'components/CreateOrganizationModal'
import EditOrganizationModal from 'components/EditOrganizationModal'
import DeleteOrganizationModal from 'components/DeleteOrganizationModal/DeleteOrganizationModal'
import { usePermissions } from 'hooks/usePermissions'
import { selectIsLoadingOrganizations, selectOrganizations, setIsInitialized, addAlert } from 'store/global'
import {
  selectDisplayedOrganization,
  setDisplayedOrganization,
  createOrganization,
  editOrganization,
  deleteOrganization,
  closeDisplayedOrganization,
} from 'store/organizations'
import messages from './messages'
import styles from './Organizations.module.scss'
import { orderBy } from 'lodash'
import { saveAs } from 'file-saver'
import { Parser } from 'json2csv'
import Search from 'UI/Search/Search'
import { Table, Empty, Button } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'

export default function Organizations() {
  const dispatch = useDispatch()
  const permissions = usePermissions()

  const isLoading = useSelector(selectIsLoadingOrganizations)
  const organizations = useSelector(selectOrganizations)
  const displayedOrganization = useSelector(selectDisplayedOrganization)

  const [sortColumn, setSortColumn] = useState(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchTerm, setSearchTerm] = useState('')

  const onCloseModal = () => dispatch(closeDisplayedOrganization())

  useEffect(() => {
    onCloseModal()
    dispatch(setIsInitialized(true))
  }, [])

  const onSort = column => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const filteredOrganizations = React.useMemo(() => {
    if (!searchTerm) return organizations
    return organizations.filter(org =>
      Object.values(org).some(value => value?.toString().toLowerCase().includes(searchTerm.toLowerCase())),
    )
  }, [organizations, searchTerm])

  const sortedOrganizations = React.useMemo(() => {
    if (!sortColumn) return filteredOrganizations
    return orderBy(filteredOrganizations, [sortColumn], [sortOrder])
  }, [filteredOrganizations, sortColumn, sortOrder])

  const onSubmit = async organization => {
    try {
      if (displayedOrganization.action === 'delete') {
        const deleteResult = await dispatch(deleteOrganization({ organizationId: organization.id }))
        if (deleteOrganization.fulfilled.match(deleteResult)) {
          dispatch(addAlert({ type: 'success', message: 'Organization deleted successfully' }))
        } else {
          dispatch(addAlert({ type: 'danger', message: 'Failed to delete organization' }))
          return
        }
      } else if (displayedOrganization.action === 'create') {
        const domains = [{ name: organization.domains }]
        const createResult = await dispatch(
          createOrganization({ organization: { name: organization.name, alias: organization.alias, domains } }),
        )

        if (createOrganization.fulfilled.match(createResult)) {
          dispatch(addAlert({ type: 'success', message: 'Organization created successfully' }))
        } else if (createOrganization.rejected.match(createResult)) {
          const error = createResult.payload
          const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create organization'

          dispatch(addAlert({ type: 'danger', message: errorMessage }))
          return
        }
      } else if (displayedOrganization.action === 'edit') {
        const editResult = await dispatch(editOrganization({ organization }))
        if (editOrganization.fulfilled.match(editResult)) {
          dispatch(addAlert({ type: 'success', message: 'Organization updated successfully' }))
        } else {
          dispatch(addAlert({ type: 'danger', message: 'Failed to update organization' }))
          return
        }
      }

      dispatch(setDisplayedOrganization())
    } catch (error) {
      dispatch(addAlert({ type: 'danger', message: 'An unexpected error occurred' }))
    }
  }

  const onDisplayModal = (action = 'create', existingOrganization = {}) => {
    const nextDisplayedOrganization = {
      ...existingOrganization,
      action,
      isOpen: true,
    }

    dispatch(setDisplayedOrganization({ organization: nextDisplayedOrganization }))
  }

  const onExportCSV = () => {
    if (!sortedOrganizations.length) return

    const exportData = sortedOrganizations.map(org => ({
      name: org.name || '',
      alias: org.alias || '',
      domains: org.domains?.map(domain => domain.name).join(', ') || '',
    }))
    const fields = ['name', 'alias', 'domains']
    const parser = new Parser({ fields })
    const csv = parser.parse(exportData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    saveAs(blob, 'organizations.csv')
  }

  const tableColumns = [
    {
      title: <FormattedMessage {...messages.columns.name} />,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: sortColumn === 'name' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('name'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.alias} />,
      dataIndex: 'alias',
      key: 'alias',
      sorter: true,
      sortOrder: sortColumn === 'alias' ? (sortOrder === 'asc' ? 'ascend' : 'descend') : null,
      onHeaderCell: () => ({
        onClick: () => onSort('alias'),
        style: { cursor: 'pointer' },
      }),
    },
    {
      title: <FormattedMessage {...messages.columns.domains} />,
      dataIndex: 'domains',
      key: 'domains',
      render: domains => domains.map(domain => domain.name).join(', '),
    },
    {
      title: <FormattedMessage {...messages.columns.actions} />,
      key: 'actions',
      render: (_, record) => {
        if (!permissions.admin) {
          return null
        }
        return (
          <div className={styles.actions}>
            <div onClick={() => onDisplayModal('delete', record)} className={styles.action}>
              <DeleteOutlined />
            </div>
            <div onClick={() => onDisplayModal('edit', record)} className={styles.action}>
              <EditOutlined />
            </div>
          </div>
        )
      },
    },
  ]

  const renderModal = () => {
    if (!displayedOrganization.isOpen) return null

    switch (displayedOrganization.action) {
      case 'create':
        return <CreateOrganizationModal onSubmit={onSubmit} />
      case 'edit':
        return <EditOrganizationModal onSubmit={onSubmit} />
      case 'delete':
        return (
          <DeleteOrganizationModal organization={displayedOrganization} onSubmit={onSubmit} onClose={onCloseModal} />
        )
      default:
        return null
    }
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerActions}>
        {permissions.admin && (
          <Button onClick={() => onDisplayModal('create')}>
            <PlusOutlined /> New Organization
          </Button>
        )}
        <div className={styles.headerActionsRight}>
          <Search
            placeholder='Search for...'
            enterButton
            allowClear
            style={{ width: 264 }}
            onSearch={value => setSearchTerm(value)}
          />
          <Button onClick={onExportCSV} disabled={!sortedOrganizations.length} type='primary'>
            <UploadOutlined /> Export CSV
          </Button>
        </div>
      </div>

      <Table
        columns={tableColumns}
        dataSource={sortedOrganizations}
        loading={isLoading}
        rowKey='id'
        scroll={{ y: 550 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: false,
        }}
        locale={{
          emptyText: <Empty description='No organizations found' />,
        }}
      />

      {renderModal()}
    </div>
  )
}
